import { useState } from 'react';
import { Button, Modal } from '../ui';
import { messagesAPI, templatesAPI, type MessageType, type MessageChannel } from '../../services/api';

// Map frontend channel names to API channel names
type FrontendChannel = 'email' | 'text' | 'letter';
const CHANNEL_MAP: Record<FrontendChannel, MessageChannel> = {
  'email': 'email',
  'text': 'sms',
  'letter': 'letter',
};

interface ContactContext {
  id: string;
  first_name: string;
  last_name?: string;
  email?: string;
  phone?: string;
  company?: string;
  stage: string;
  notes?: string;
  last_contact_at?: number;
  ai_summary?: string;
  recent_interactions?: {
    type: string;
    subject?: string;
    snippet?: string;
    occurred_at: number;
  }[];
}

interface DraftMessageModalProps {
  open: boolean;
  onClose: () => void;
  contact: ContactContext;
}

const MESSAGE_TEMPLATES: { id: MessageType; label: string }[] = [
  { id: 'check_in', label: 'Check-in' },
  { id: 'follow_up', label: 'Follow up' },
  { id: 'market_update', label: 'Market update' },
  { id: 'birthday', label: 'Birthday' },
  { id: 'anniversary', label: 'Home anniversary' },
  { id: 'referral_ask', label: 'Ask for referral' },
  { id: 'just_listed', label: 'Just listed' },
  { id: 'thank_you', label: 'Thank you' },
  { id: 'custom', label: 'Custom...' },
];

export function DraftMessageModal({ open, onClose, contact }: DraftMessageModalProps) {
  const [channel, setChannel] = useState<FrontendChannel>('email');
  const [selectedTemplate, setSelectedTemplate] = useState<MessageType | null>(null);
  const [customPrompt, setCustomPrompt] = useState('');
  const [generatedDraft, setGeneratedDraft] = useState('');
  const [generatedSubject, setGeneratedSubject] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSavingTemplate, setIsSavingTemplate] = useState(false);
  const [templateSaved, setTemplateSaved] = useState(false);

  const contactName = `${contact.first_name} ${contact.last_name || ''}`.trim();

  const handleClose = () => {
    setSelectedTemplate(null);
    setCustomPrompt('');
    setGeneratedDraft('');
    setGeneratedSubject(null);
    setSuggestions([]);
    setIsGenerating(false);
    setCopied(false);
    setError(null);
    setIsSavingTemplate(false);
    setTemplateSaved(false);
    onClose();
  };

  const handleGenerate = async () => {
    if (!selectedTemplate) return;
    if (selectedTemplate === 'custom' && !customPrompt) return;

    setIsGenerating(true);
    setGeneratedDraft('');
    setGeneratedSubject(null);
    setSuggestions([]);
    setError(null);

    try {
      const response = await messagesAPI.draftMessage({
        contact_id: contact.id,
        message_type: selectedTemplate,
        channel: CHANNEL_MAP[channel],
        context: selectedTemplate === 'custom' ? customPrompt : undefined,
      });

      if (response.success && response.data) {
        setGeneratedDraft(response.data.draft);
        setGeneratedSubject(response.data.subject);
        setSuggestions(response.data.suggestions);
      } else {
        setError(response.error?.message || 'Failed to generate draft. Please try again.');
      }
    } catch (err) {
      console.error('Failed to generate draft:', err);
      setError('Failed to connect to AI service. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCopy = async () => {
    await navigator.clipboard.writeText(generatedDraft);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleOpenEmail = () => {
    const subject = encodeURIComponent(generatedSubject || 'Hello');
    const body = encodeURIComponent(generatedDraft);
    window.open(`mailto:${contact.email}?subject=${subject}&body=${body}`, '_self');
  };

  const handleOpenSMS = () => {
    const body = encodeURIComponent(generatedDraft);
    window.open(`sms:${contact.phone}?body=${body}`, '_self');
  };

  const handleSaveAsTemplate = async () => {
    if (!generatedDraft) return;

    const templateName = prompt('Enter a name for this template:');
    if (!templateName) return;

    setIsSavingTemplate(true);
    setError(null);

    try {
      await templatesAPI.create({
        name: templateName,
        category: selectedTemplate === 'custom' ? 'other' : selectedTemplate!,
        channel: CHANNEL_MAP[channel],
        subject: channel === 'email' ? generatedSubject || undefined : undefined,
        body: generatedDraft,
      });

      setTemplateSaved(true);
      setTimeout(() => setTemplateSaved(false), 3000);
    } catch (err) {
      console.error('Failed to save template:', err);
      setError('Failed to save template. Please try again.');
    } finally {
      setIsSavingTemplate(false);
    }
  };

  return (
    <Modal open={open} onClose={handleClose} title={`Draft Message to ${contactName}`} size="lg">
      <div className="px-6 pb-6">
        {/* Contact Context Card */}
        <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="font-medium text-gray-900 dark:text-white">{contactName}</h3>
              {contact.company && (
                <p className="text-sm text-gray-500 dark:text-gray-400">{contact.company}</p>
              )}
            </div>
            <span className="text-xs px-2 py-1 rounded-full bg-ondeck-100 dark:bg-ondeck-900/50 text-ondeck-700 dark:text-ondeck-300 capitalize">
              {contact.stage.replace('_', ' ')}
            </span>
          </div>
          {contact.ai_summary && (
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400 italic">
              "{contact.ai_summary}"
            </p>
          )}
          {contact.recent_interactions?.[0] && (
            <p className="mt-2 text-xs text-gray-500 dark:text-gray-500">
              Last: {contact.recent_interactions[0].type} - {contact.recent_interactions[0].snippet?.substring(0, 50)}...
            </p>
          )}
        </div>

        {!generatedDraft ? (
          <>
            {/* Message Type */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Message Type
              </label>
              <div className="flex gap-2">
                {(['email', 'text', 'letter'] as FrontendChannel[]).map((type) => (
                  <button
                    key={type}
                    onClick={() => setChannel(type)}
                    className={`flex-1 py-2 px-4 rounded-lg border text-sm font-medium transition-colors ${
                      channel === type
                        ? 'border-ondeck-500 bg-ondeck-50 dark:bg-ondeck-900/30 text-ondeck-700 dark:text-ondeck-300'
                        : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    {type === 'email' && 'Email'}
                    {type === 'text' && 'Text'}
                    {type === 'letter' && 'Letter'}
                  </button>
                ))}
              </div>
            </div>

            {/* Templates */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                What do you want to say?
              </label>
              <div className="grid grid-cols-3 gap-2">
                {MESSAGE_TEMPLATES.map((template) => (
                  <button
                    key={template.id}
                    onClick={() => setSelectedTemplate(template.id)}
                    className={`py-2 px-3 rounded-lg border text-sm transition-colors ${
                      selectedTemplate === template.id
                        ? 'border-ondeck-500 bg-ondeck-50 dark:bg-ondeck-900/30 text-ondeck-700 dark:text-ondeck-300'
                        : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    {template.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Custom Prompt */}
            {selectedTemplate === 'custom' && (
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Describe what you want to say
                </label>
                <textarea
                  value={customPrompt}
                  onChange={(e) => setCustomPrompt(e.target.value)}
                  rows={3}
                  placeholder="e.g., Congratulate them on their new job and ask if the move has them thinking about real estate..."
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-ondeck-500 focus:border-ondeck-500"
                />
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
              </div>
            )}

            {/* Generate Button */}
            <Button
              onClick={handleGenerate}
              disabled={!selectedTemplate || (selectedTemplate === 'custom' && !customPrompt)}
              loading={isGenerating}
              className="w-full"
            >
              {isGenerating ? 'Generating...' : 'Generate Draft'}
            </Button>

            <p className="mt-3 text-xs text-center text-gray-500 dark:text-gray-400">
              Powered by AI - Uses contact context to personalize
            </p>
          </>
        ) : (
          <>
            {/* Generated Subject (for email) */}
            {channel === 'email' && generatedSubject && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Subject Line
                </label>
                <input
                  type="text"
                  value={generatedSubject}
                  onChange={(e) => setGeneratedSubject(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-ondeck-500 focus:border-ondeck-500"
                />
              </div>
            )}

            {/* Generated Draft */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Generated Draft
                </label>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {channel === 'text' ? `${generatedDraft.length}/160 chars` : ''}
                </span>
              </div>
              <textarea
                value={generatedDraft}
                onChange={(e) => setGeneratedDraft(e.target.value)}
                rows={channel === 'text' ? 4 : 10}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-ondeck-500 focus:border-ondeck-500 font-mono text-sm"
              />
            </div>

            {/* Suggestions */}
            {suggestions.length > 0 && (
              <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                <p className="text-xs font-medium text-blue-700 dark:text-blue-300 mb-1">
                  Alternative approaches:
                </p>
                <ul className="text-xs text-blue-600 dark:text-blue-400 list-disc list-inside">
                  {suggestions.map((suggestion, index) => (
                    <li key={index}>{suggestion}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-2 mb-4">
              <Button variant="outline" onClick={handleCopy} className="flex-1">
                {copied ? 'Copied!' : 'Copy to Clipboard'}
              </Button>
              {channel === 'email' && contact.email && (
                <Button onClick={handleOpenEmail} className="flex-1">
                  Open in Email
                </Button>
              )}
              {channel === 'text' && contact.phone && (
                <Button onClick={handleOpenSMS} className="flex-1">
                  Open in Messages
                </Button>
              )}
            </div>

            {/* Save as Template */}
            <div className="mb-4">
              <Button 
                variant="outline" 
                onClick={handleSaveAsTemplate} 
                loading={isSavingTemplate}
                className="w-full"
              >
                {templateSaved ? '✓ Saved as Template' : 'Save as Template'}
              </Button>
            </div>

            {/* Regenerate / Back */}
            <div className="flex gap-2">
              <Button
                variant="ghost"
                onClick={() => {
                  setGeneratedDraft('');
                  setGeneratedSubject(null);
                  setSuggestions([]);
                  setSelectedTemplate(null);
                }}
                className="flex-1"
              >
                Start Over
              </Button>
              <Button variant="outline" onClick={handleGenerate} loading={isGenerating} className="flex-1">
                Regenerate
              </Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
